import bigTaxiStartupLogo from '../../assets/big-taxi-driver-logo.png';
import './DriverStartup.css';
import FleetDriverApp from '../fleetDriver/FleetDriverApp';
import {fleetSession} from '../fleetDriver/fleetDriverApi';
import DriverDailyReport from '../../components/dailyReport/DriverDailyReport';
/**
 * 司机端路由入口
 * 管理司机端的认证和路由
 * 
 * ⚠️ CRITICAL: 使用集中式API配置和Axios拦截器 ⚠️
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import DriverLogin from './DriverLogin';
import DriverOrders from './DriverOrders';
import DriverOrderDetail from './DriverOrderDetail';
import DriverTripChat from './DriverTripChat';
import DriverSettings from './DriverSettings';
import DriverLegalCompliance from './DriverLegalCompliance';
import DriverRegistration from './DriverRegistration';
import DriverMaintenance from './DriverMaintenance';
import { DriverAPI, TokenManager, setGlobalLogoutHandler, API_BASE_URL } from './config';
import { getDriverLanguage, getDriverText, getLocalizedDriverNotice } from './driverI18n';
import { getDriverFeatureText } from './driverFeatureI18n';
import { initializeDriverPushNotifications, registerDriverPushToken } from '../../services/driverApp/pushService';
import { clearDriverOfflineOrderPacks } from '../../services/driverApp/offlineOrderPackService';

const DRIVER_HORIZONTAL_SWIPE_BACK_ENABLED = false;

export default function DriverApp() {
  const [startupVisible,setStartupVisible]=useState(true);
  useEffect(()=>{const timer=setTimeout(()=>setStartupVisible(false),1200);return()=>clearTimeout(timer);},[]);
  const [companySession, setCompanySession] = useState(fleetSession);
  useEffect(() => {
    const update = () => setCompanySession(fleetSession());
    window.addEventListener('fleet-driver-session-changed', update);
    return () => window.removeEventListener('fleet-driver-session-changed', update);
  }, []);
  // Mount only one realm: Shonan polling, push and routes never run for company drivers.
  return <>{companySession ? <FleetDriverApp /> : <ShonanDriverApp />}{startupVisible&&<div className="big-taxi-startup" aria-label="BIG TAXI"><img src={bigTaxiStartupLogo} alt="BIG TAXI"/><strong>BIG TAXI</strong><span>SAFER JOURNEYS · A BRIGHTER TOMORROW</span></div>}</>;
}

function ShonanDriverApp() {
  const [driver, setDriver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [driverNotices, setDriverNotices] = useState([]);
  const [activeNotice, setActiveNotice] = useState(null);
  const [importantNotifications, setImportantNotifications] = useState([]);
  const [notificationsPaused, setNotificationsPaused] = useState(false);
  const [notificationPendingCount, setNotificationPendingCount] = useState(0);
  const [notificationError, setNotificationError] = useState(false);
  const notificationRequests = useRef(new Set());
  const confirmedNotifications = useRef(new Set());
  const pausedNotifications = useRef(false);
  const activeImportantNotification = notificationsPaused ? null : importantNotifications.find(item => !notificationRequests.current.has(item.id)) || null;
  const [swipeBackTargetPath, setSwipeBackTargetPath] = useState('');
  const swipeBackRef = useRef(null);
  const swipeBackCommittingRef = useRef(false);
  const routeTransitionRef = useRef(null);
  const swipeBackUnderlayRef = useRef(null);
  const swipeBackShadeRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const featureText = getDriverFeatureText(getDriverLanguage());

  // 全局登出处理器 - 供Axios拦截器调用
  const handleDriverLogout = useCallback(() => {
    console.log('[DriverApp] handleDriverLogout called');
    const driverId = driver?.id || TokenManager.getDriverInfo()?.id;
    clearDriverOfflineOrderPacks(driverId);
    TokenManager.clearAll();
    setDriver(null);
    navigate('/driver', { replace: true });
  }, [driver?.id, navigate]);

  // 注册全局登出处理器
  useEffect(() => {
    setGlobalLogoutHandler(handleDriverLogout);
    console.log('[DriverApp] Global logout handler registered');
    console.log('[DriverApp] Using API:', API_BASE_URL);
  }, [handleDriverLogout]);

  const checkDriverAuth = useCallback(() => {
    const token = TokenManager.getToken();
    const driverInfo = TokenManager.getDriverInfo();
    
    if (token && driverInfo) {
      setDriver(driverInfo);
      return driverInfo;
    }
    
    // 清理无效状态
    if (token && !driverInfo) {
      TokenManager.clearAll();
    }
    
    setLoading(false);
    return null;
  }, []);

  useEffect(() => {
    const driverData = checkDriverAuth();
    setLoading(false);

    const refreshDriverProfile = async () => {
      if (!driverData) return;
      try {
        const latestDriver = await DriverAPI.getMe();
        setDriver(latestDriver);
        TokenManager.setDriverInfo(latestDriver);
      } catch (error) {
        console.warn('[DriverApp] driver profile refresh skipped:', error);
      }
    };
    
    // 如果已登录且在登录页，跳转到订单页
      if (driverData && location.pathname === '/driver') {
        navigate('/driver/orders', { replace: true });
      }
      if (driverData) {
        refreshDriverProfile();
        initializeDriverPushNotifications(DriverAPI).catch((error) => {
          console.warn('[DriverApp] Push initialization skipped:', error);
        });
      }
    }, [checkDriverAuth, location.pathname, navigate]);

  const handleDriverLogin = (driverData, token) => {
    console.log('[DriverApp] handleDriverLogin called', { driverId: driverData?.id, hasToken: !!token });
    
    // 使用TokenManager存储凭据
    if (token) {
      TokenManager.setToken(token);
      console.log('[DriverApp] Token saved via TokenManager');
    }
    if (driverData) {
      TokenManager.setDriverInfo(driverData);
      console.log('[DriverApp] Driver info saved via TokenManager');
    }
    
    // 验证存储成功
    if (!TokenManager.isAuthenticated()) {
      console.error('[DriverApp] CRITICAL: Credentials were not saved properly!');
      alert(featureText.storageFailure);
      return;
    }
    
    setDriver(driverData);
    registerDriverPushToken((payload) => DriverAPI.registerPushToken(payload)).catch((error) => {
      console.warn('[DriverApp] Push registration skipped:', error);
    });
    
    // 登录成功后直接跳转（不依赖state更新）
    if (driverData.must_change_password) {
      console.log('[DriverApp] Redirecting to settings (must change password)');
      navigate('/driver/settings', { state: { mustChangePassword: true }, replace: true });
    } else {
      console.log('[DriverApp] Redirecting to orders');
      navigate(location.pathname === '/driver/maintenance' ? '/driver/maintenance' : '/driver/orders', { replace: true });
    }
  };

  // 检查是否已登录
  const isAuthenticated = () => {
    const authenticated = TokenManager.isAuthenticated();
    console.log('[DriverApp] isAuthenticated check:', authenticated);
    return authenticated;
  };

  // 获取当前司机信息（优先从state，否则从TokenManager）
  const getCurrentDriver = () => {
    if (driver) return driver;
    return TokenManager.getDriverInfo();
  };

  const fetchUnreadDriverNotices = useCallback(async () => {
    if (!TokenManager.isAuthenticated()) return;
    try {
      const notices = await DriverAPI.getUnreadDriverNotices();
      setDriverNotices(Array.isArray(notices) ? notices : []);
      setActiveNotice((current) => current || (Array.isArray(notices) && notices.length > 0 ? notices[0] : null));
    } catch (error) {
      console.warn('[DriverApp] Unread notice fetch skipped:', error);
    }
  }, []);

  const fetchUnreadImportantNotifications = useCallback(async () => {
    if (!TokenManager.isAuthenticated()) return;
    try {
      const notifications = await DriverAPI.getUnreadImportantNotifications();
      const list = Array.isArray(notifications) ? notifications : [];
      // An older poll may resolve after acknowledgment; never restore confirmed IDs.
      setImportantNotifications(current => [
        ...list.filter(item => !confirmedNotifications.current.has(item.id)),
        ...current.filter(item => notificationRequests.current.has(item.id) && !list.some(fresh => fresh.id === item.id)),
      ]);
    } catch (error) {
      console.warn('[DriverApp] Important notification fetch skipped:', error);
    }
  }, []);

  useEffect(() => {
    const currentDriverInfo = driver || TokenManager.getDriverInfo();
    if (!currentDriverInfo) return;
    fetchUnreadDriverNotices();
    fetchUnreadImportantNotifications();
  }, [driver, fetchUnreadDriverNotices, fetchUnreadImportantNotifications]);

  useEffect(() => {
    const currentDriverInfo = driver || TokenManager.getDriverInfo();
    if (!currentDriverInfo) return undefined;
    const timer = window.setInterval(fetchUnreadImportantNotifications, 30000);
    return () => window.clearInterval(timer);
  }, [driver, fetchUnreadImportantNotifications]);

  const handleCloseActiveNotice = async () => {
    if (!activeNotice?.id) {
      setActiveNotice(null);
      return;
    }
    try {
      await DriverAPI.markDriverNoticeRead(activeNotice.id);
    } catch (error) {
      console.warn('[DriverApp] Mark notice read skipped:', error);
    }
    const remaining = driverNotices.filter((notice) => notice.id !== activeNotice.id);
    setDriverNotices(remaining);
    setActiveNotice(remaining[0] || null);
  };

  const pauseImportantNotifications = () => {
    pausedNotifications.current = true;
    setNotificationsPaused(true);
    setNotificationError(false);
  };
  const resumeImportantNotifications = () => {
    pausedNotifications.current = false;
    setNotificationsPaused(false);
  };
  const handleAcknowledgeImportantNotification = async (shouldOpenOrder = false) => {
    if (!activeImportantNotification?.id || notificationRequests.current.has(activeImportantNotification.id)) return;
    const noticeId = activeImportantNotification.id;
    const orderId = activeImportantNotification.order_id || activeImportantNotification.data?.order_id;
    notificationRequests.current.add(noticeId);
    setNotificationPendingCount(notificationRequests.current.size);
    setNotificationError(false);
    if (shouldOpenOrder && orderId) {
      pauseImportantNotifications();
      navigate(`/driver/orders/${orderId}`);
    }
    try {
      await DriverAPI.acknowledgeNotification(activeImportantNotification.id, {
        order_id: orderId,
        acknowledged_order_at: new Date().toISOString(),
      });
      confirmedNotifications.current.add(noticeId);
      setImportantNotifications(current => current.filter(item => item.id !== noticeId));
    } catch (error) {
      console.warn('[DriverApp] Acknowledge important notification failed:', {code:error?.code,status:error?.response?.status,message:error?.message});
      setNotificationError(true);
    } finally {
      notificationRequests.current.delete(noticeId);
      setNotificationPendingCount(notificationRequests.current.size);
    }
  };

  const isOrderListPath = location.pathname === '/driver/orders';
  const isOrderDetailPath = /^\/driver\/orders\/[^/]+$/.test(location.pathname);
  const isOrderChatPath = /^\/driver\/orders\/[^/]+\/chat$/.test(location.pathname);
  const isDriverSettingsPath = location.pathname === '/driver/settings';
  const isDriverLegalPath = location.pathname === '/driver/legal';
  const shouldKeepOrdersMounted = isAuthenticated() && (
    isOrderListPath || isOrderDetailPath || isOrderChatPath || isDriverSettingsPath || isDriverLegalPath
  );
  const overlayLocation = (isOrderDetailPath || isOrderChatPath || isDriverSettingsPath || isDriverLegalPath) ? location : null;

  const getSwipeBackTarget = useCallback((pathname) => {
    if (/^\/driver\/orders\/[^/]+\/chat$/.test(pathname)) {
      return pathname.replace(/\/chat$/, '');
    }
    if (/^\/driver\/orders\/[^/]+$/.test(pathname)) {
      return '/driver/orders';
    }
    if (pathname === '/driver/settings') {
      return '/driver/orders';
    }
    if (pathname === '/driver/legal') {
      return '/driver/orders';
    }
    return '';
  }, []);

  const shouldIgnoreSwipeBack = useCallback((target) => {
    return Boolean(target?.closest?.(
      [
        'button',
        'a',
        'input',
        'select',
        'textarea',
        '[role="button"]',
        '.driver-status-swipe',
        '.driver-fixed-overlay',
        '.driver-map-confirm-panel',
        '.driver-issue-report-panel',
      ].join(',')
    ));
  }, []);

    useEffect(() => {
      if (!DRIVER_HORIZONTAL_SWIPE_BACK_ENABLED) {
        document.documentElement.style.setProperty('--driver-swipe-back-x', '0px');
        document.documentElement.style.setProperty('--driver-swipe-back-opacity', '1');
        document.body.classList.remove('driver-swipe-back-active');
        document.body.classList.remove('driver-swipe-back-dragging');
        document.body.classList.remove('driver-swipe-back-returning');
        document.body.classList.remove('driver-swipe-back-committing');
        swipeBackRef.current = null;
        setSwipeBackTargetPath('');
        return undefined;
      }

      const targetPath = getSwipeBackTarget(location.pathname);
      if (!targetPath) return undefined;

    const clearRouteSwipeStyle = () => {
      const route = routeTransitionRef.current;
      const underlay = swipeBackUnderlayRef.current;
      const shade = swipeBackShadeRef.current;
      if (route) {
        route.style.animation = '';
        route.style.transition = '';
        route.style.transform = '';
        route.style.opacity = '';
        route.style.filter = '';
        route.style.boxShadow = '';
      }
      if (underlay) {
        underlay.style.transition = '';
        underlay.style.transform = '';
        underlay.style.opacity = '';
      }
      if (shade) {
        shade.style.transition = '';
        shade.style.opacity = '0';
      }
    };

    const resetSwipeBackVisual = () => {
      document.documentElement.style.setProperty('--driver-swipe-back-x', '0px');
      document.documentElement.style.setProperty('--driver-swipe-back-opacity', '1');
      document.body.classList.remove('driver-swipe-back-active');
      document.body.classList.remove('driver-swipe-back-dragging');
      document.body.classList.remove('driver-swipe-back-returning');
      document.body.classList.remove('driver-swipe-back-committing');
      clearRouteSwipeStyle();
      setSwipeBackTargetPath('');
    };

    const updateSwipeBackVisual = (dx) => {
      const visualX = Math.max(0, Math.min(dx, window.innerWidth * 0.9));
      const progress = Math.min(1, visualX / Math.max(1, window.innerWidth * 0.62));
      const roundedX = Math.round(visualX);
      const route = routeTransitionRef.current;
      const underlay = swipeBackUnderlayRef.current;
      const shade = swipeBackShadeRef.current;
      document.documentElement.style.setProperty('--driver-swipe-back-x', `${Math.round(visualX)}px`);
      document.documentElement.style.setProperty('--driver-swipe-back-opacity', '1');
      document.body.classList.add('driver-swipe-back-dragging');
      if (visualX > 12) {
        document.body.classList.add('driver-swipe-back-active');
      }
      if (route) {
        route.style.animation = 'none';
        route.style.transition = 'none';
        route.style.transform = `translate3d(${roundedX}px, 0, 0)`;
        route.style.opacity = '1';
        route.style.filter = visualX > 8 ? 'brightness(1.03)' : '';
        route.style.boxShadow = visualX > 8 ? '-26px 0 46px -34px rgba(0, 0, 0, 0.95)' : '';
      }
      if (underlay) {
        underlay.style.transition = 'none';
        underlay.style.opacity = '1';
        underlay.style.transform = `translate3d(${Math.round(-34 + progress * 34)}px, 0, 0) scale(${0.96 + progress * 0.04})`;
      }
      if (shade) {
        shade.style.transition = 'none';
        shade.style.opacity = '0';
      }
    };

    const handleTouchStart = (event) => {
      const touch = event.touches?.[0];
      if (!touch || touch.clientX > Math.min(window.innerWidth * 0.72, 360) || shouldIgnoreSwipeBack(event.target)) {
        swipeBackRef.current = null;
        resetSwipeBackVisual();
        return;
      }

      const hasPersistentOrdersBackground = targetPath === '/driver/orders'
        && (/^\/driver\/orders\/[^/]+$/.test(location.pathname) || location.pathname === '/driver/settings');
      setSwipeBackTargetPath(hasPersistentOrdersBackground ? '' : targetPath);
      swipeBackRef.current = {
        startX: touch.clientX,
        startY: touch.clientY,
        startedAt: Date.now(),
        dragging: false,
      };
    };

    const handleTouchMove = (event) => {
      const gesture = swipeBackRef.current;
      const touch = event.touches?.[0];
      if (!gesture || !touch) return;

      const dx = touch.clientX - gesture.startX;
      const dy = touch.clientY - gesture.startY;
      if (dx <= 0) return;

      if (Math.abs(dy) > 34 && Math.abs(dy) > dx * 1.15) {
        swipeBackRef.current = null;
        resetSwipeBackVisual();
        return;
      }

      if (dx > 8 && dx > Math.abs(dy) * 1.05) {
        event.preventDefault();
        gesture.dragging = true;
        updateSwipeBackVisual(dx);
      }
    };

    const handleTouchEnd = (event) => {
      const gesture = swipeBackRef.current;
      const touch = event.changedTouches?.[0];
      swipeBackRef.current = null;
      if (!gesture || !touch) return;

      const dx = touch.clientX - gesture.startX;
      const dy = touch.clientY - gesture.startY;
      const elapsed = Date.now() - gesture.startedAt;
      const isBackSwipe = dx >= 76 && Math.abs(dy) <= 58 && dx > Math.abs(dy) * 1.35 && elapsed <= 900;
      if (isBackSwipe) {
        const route = routeTransitionRef.current;
        const underlay = swipeBackUnderlayRef.current;
        const shade = swipeBackShadeRef.current;
        document.documentElement.style.setProperty('--driver-swipe-back-x', `${window.innerWidth}px`);
        document.documentElement.style.setProperty('--driver-swipe-back-opacity', '1');
        if (route) {
          route.style.animation = 'none';
          route.style.transition = 'transform 180ms cubic-bezier(0.16, 0.9, 0.22, 1), filter 180ms ease';
          route.style.transform = `translate3d(${window.innerWidth}px, 0, 0)`;
          route.style.opacity = '1';
          route.style.filter = 'brightness(1.04)';
        }
        if (underlay) {
          underlay.style.transition = 'transform 180ms cubic-bezier(0.16, 0.9, 0.22, 1)';
          underlay.style.transform = 'translate3d(0px, 0, 0) scale(1)';
          underlay.style.opacity = '1';
        }
        if (shade) {
          shade.style.transition = 'none';
          shade.style.opacity = '0';
        }
        window.setTimeout(() => {
          swipeBackCommittingRef.current = true;
          document.body.classList.add('driver-swipe-back-committing');
          navigate(targetPath, { state: { driverSwipeBackReturn: true } });
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
              window.setTimeout(() => {
                resetSwipeBackVisual();
                swipeBackCommittingRef.current = false;
              }, 360);
            });
          });
        }, 180);
        return;
      }
      const route = routeTransitionRef.current;
      const underlay = swipeBackUnderlayRef.current;
      const shade = swipeBackShadeRef.current;
      document.body.classList.add('driver-swipe-back-returning');
      document.documentElement.style.setProperty('--driver-swipe-back-x', '0px');
      document.documentElement.style.setProperty('--driver-swipe-back-opacity', '1');
      if (route) {
        route.style.animation = 'none';
        route.style.transition = 'transform 180ms cubic-bezier(0.2, 0.9, 0.2, 1), filter 180ms ease';
        route.style.transform = 'translate3d(0px, 0, 0)';
        route.style.opacity = '1';
        route.style.filter = '';
        route.style.boxShadow = '';
      }
      if (underlay) {
        underlay.style.transition = 'transform 180ms cubic-bezier(0.2, 0.9, 0.2, 1)';
        underlay.style.transform = 'translate3d(-34px, 0, 0) scale(0.96)';
        underlay.style.opacity = '0';
      }
      if (shade) {
        shade.style.transition = 'none';
        shade.style.opacity = '0';
      }
      window.setTimeout(resetSwipeBackVisual, 190);
    };

    const handleTouchCancel = () => {
      swipeBackRef.current = null;
      resetSwipeBackVisual();
    };

    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });
    window.addEventListener('touchcancel', handleTouchCancel, { passive: true });

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchcancel', handleTouchCancel);
      if (!swipeBackCommittingRef.current) {
        resetSwipeBackVisual();
      }
    };
  }, [getSwipeBackTarget, location.pathname, navigate, shouldIgnoreSwipeBack]);

  if (loading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="text-muted-foreground">{getDriverText(getDriverLanguage()).loading}</div>
      </div>
    );
  }

  const currentDriver = getCurrentDriver();
  const currentLanguage = getDriverLanguage();
  const text = getDriverText(currentLanguage);
  const renderDriverOrdersRoute = () => (
    <Routes location={{ pathname: '/driver/orders' }}>
      <Route
        path="/orders"
        element={
          !isAuthenticated() ? (
            <Navigate to="/driver" replace />
          ) : (
            <DriverOrders driver={currentDriver} onLogout={handleDriverLogout} />
          )
        }
      />
    </Routes>
  );

  const renderDriverRoutes = (routesLocation) => (
    <Routes location={routesLocation}>
      <Route path="/maintenance" element={<DriverMaintenance />} />
      {/* 登录页 */}
      <Route
        path="/"
        element={
          isAuthenticated() ? (
            <Navigate to="/driver/orders" replace />
          ) : (
            <DriverLogin onLogin={handleDriverLogin} onRegister={() => navigate('/driver/register')} />
          )
        }
      />

      <Route
        path="/register"
        element={
          isAuthenticated() ? <Navigate to="/driver/orders" replace /> : (
            <DriverRegistration onBack={() => navigate('/driver')} onCompleted={() => {}} />
          )
        }
      />

      {/* 订单列表 */}
      <Route
        path="/orders"
        element={
          !isAuthenticated() ? (
            <Navigate to="/driver" replace />
          ) : (
            <DriverOrders driver={currentDriver} onLogout={handleDriverLogout} />
          )
        }
      />

      {/* 订单详情 */}
      <Route
        path="/orders/:orderId"
        element={
          !isAuthenticated() ? (
            <Navigate to="/driver" replace />
          ) : (
            <DriverOrderDetail driver={currentDriver} onLogout={handleDriverLogout} />
          )
        }
      />

      <Route
        path="/orders/:orderId/chat"
        element={
          !isAuthenticated() ? (
            <Navigate to="/driver" replace />
          ) : (
            <DriverTripChat />
          )
        }
      />

      {/* 设置 */}
      <Route
        path="/settings"
        element={
          !isAuthenticated() ? (
            <Navigate to="/driver" replace />
          ) : (
            <DriverSettings driver={currentDriver} onLogout={handleDriverLogout} />
          )
        }
      />

      <Route
        path="/legal"
        element={
          !isAuthenticated() ? (
            <Navigate to="/driver" replace />
          ) : (
            <DriverLegalCompliance driver={currentDriver} />
          )
        }
      />

      {/* 默认重定向 */}
      <Route path="*" element={<Navigate to="/driver" replace />} />
    </Routes>
  );

  const renderDriverOverlayRoutes = (routesLocation) => (
    <Routes location={routesLocation}>
      <Route
        path="/orders/:orderId"
        element={
          !isAuthenticated() ? (
            <Navigate to="/driver" replace />
          ) : (
            <DriverOrderDetail driver={currentDriver} onLogout={handleDriverLogout} />
          )
        }
      />

      <Route
        path="/orders/:orderId/chat"
        element={
          !isAuthenticated() ? (
            <Navigate to="/driver" replace />
          ) : (
            <DriverTripChat />
          )
        }
      />

      <Route
        path="/settings"
        element={
          !isAuthenticated() ? (
            <Navigate to="/driver" replace />
          ) : (
            <DriverSettings driver={currentDriver} onLogout={handleDriverLogout} />
          )
        }
      />

      <Route
        path="/legal"
        element={
          !isAuthenticated() ? (
            <Navigate to="/driver" replace />
          ) : (
            <DriverLegalCompliance driver={currentDriver} />
          )
        }
      />
    </Routes>
  );

  if (shouldKeepOrdersMounted) {
    return (
      <div className="driver-luxury-portal driver-orders-stack">
        {driver?.id && <DriverDailyReport driverId={driver.id} blocked={Boolean(activeImportantNotification || activeNotice)} />}
        <div className="driver-orders-background">
          {renderDriverOrdersRoute()}
        </div>
        {swipeBackTargetPath && (
          <>
            <div ref={swipeBackUnderlayRef} className="driver-swipe-back-underlay" aria-hidden="true">
              {renderDriverRoutes({ pathname: swipeBackTargetPath })}
            </div>
            <div ref={swipeBackShadeRef} className="driver-swipe-back-shade" aria-hidden="true" />
          </>
        )}
        {overlayLocation && (
          <div
            ref={routeTransitionRef}
            className={`driver-route-transition driver-route-overlay ${location.state?.driverSwipeBackReturn ? 'driver-route-transition-no-enter' : ''}`}
            key={location.pathname}
          >
            {renderDriverOverlayRoutes(location)}
          </div>
        )}
        {activeImportantNotification && (
          <DriverImportantNotificationModal
            key={activeImportantNotification.id}
            notification={activeImportantNotification}
            text={text}
            language={currentLanguage}
            busy={false} error={notificationError} count={importantNotifications.length}
            onLater={pauseImportantNotifications}
            onAcknowledge={() => handleAcknowledgeImportantNotification(false)}
            onOpenOrder={() => handleAcknowledgeImportantNotification(true)}
          />
        )}
        {notificationPendingCount > 0 && createPortal(
          <div role="status" data-testid="driver-notification-pending" className="fixed left-4 z-[80] rounded-full bg-stone-950 px-4 py-2 text-sm text-amber-200"
            style={{bottom:'calc(env(safe-area-inset-bottom, 0px) + 140px)',pointerEvents:'none'}}>
            {text.importantNotificationSaving} · {notificationPendingCount}
          </div>, document.body
        )}
        {notificationsPaused && importantNotifications.length > 0 && createPortal(
          <button type="button" data-testid="driver-notification-resume" onClick={resumeImportantNotifications}
            className="fixed right-4 z-[80] rounded-full border border-amber-300/40 bg-stone-950 px-4 py-2 text-sm text-amber-200 shadow-xl"
            style={{bottom:'calc(env(safe-area-inset-bottom, 0px) + 90px)',pointerEvents:'auto'}}>
            {text.importantNotificationTitle} · {importantNotifications.length}
          </button>, document.body
        )}
        {!activeImportantNotification && activeNotice && (
          <DriverNoticeModal notice={activeNotice} text={text} onClose={handleCloseActiveNotice} />
        )}
      </div>
    );
  }

  return (
    <div className="driver-luxury-portal">
      {driver?.id && <DriverDailyReport driverId={driver.id} blocked={Boolean(activeImportantNotification || activeNotice)} />}
      {swipeBackTargetPath && (
        <>
          <div ref={swipeBackUnderlayRef} className="driver-swipe-back-underlay" aria-hidden="true">
            {renderDriverRoutes({ pathname: swipeBackTargetPath })}
          </div>
          <div ref={swipeBackShadeRef} className="driver-swipe-back-shade" aria-hidden="true" />
        </>
      )}
      <div
        ref={routeTransitionRef}
        className={`driver-route-transition ${location.state?.driverSwipeBackReturn ? 'driver-route-transition-no-enter' : ''}`}
        key={location.pathname}
      >
        {renderDriverRoutes(location)}
      </div>
      {activeImportantNotification && (
        <DriverImportantNotificationModal
          key={activeImportantNotification.id}
            notification={activeImportantNotification}
          text={text}
          language={currentLanguage}
          busy={false} error={notificationError} count={importantNotifications.length}
          onLater={pauseImportantNotifications}
          onAcknowledge={() => handleAcknowledgeImportantNotification(false)}
          onOpenOrder={() => handleAcknowledgeImportantNotification(true)}
        />
      )}
      {notificationPendingCount > 0 && createPortal(
          <div role="status" data-testid="driver-notification-pending" className="fixed left-4 z-[80] rounded-full bg-stone-950 px-4 py-2 text-sm text-amber-200"
            style={{bottom:'calc(env(safe-area-inset-bottom, 0px) + 140px)',pointerEvents:'none'}}>
            {text.importantNotificationSaving} · {notificationPendingCount}
          </div>, document.body
        )}
      {notificationsPaused && importantNotifications.length > 0 && createPortal(
          <button type="button" data-testid="driver-notification-resume" onClick={resumeImportantNotifications}
            className="fixed right-4 z-[80] rounded-full border border-amber-300/40 bg-stone-950 px-4 py-2 text-sm text-amber-200 shadow-xl"
            style={{bottom:'calc(env(safe-area-inset-bottom, 0px) + 90px)',pointerEvents:'auto'}}>
            {text.importantNotificationTitle} · {importantNotifications.length}
          </button>, document.body
        )}
      {!activeImportantNotification && activeNotice && (
        <DriverNoticeModal notice={activeNotice} text={text} onClose={handleCloseActiveNotice} />
      )}
    </div>
  );
}

export function DriverImportantNotificationModal({ notification, text, language, busy, error, count, onLater, onAcknowledge, onOpenOrder }) {
  const locale = language === 'ja' ? 'ja-JP' : language === 'en' ? 'en-US' : language === 'zh_tw' ? 'zh-TW' : 'zh-CN';
  return createPortal(
    <div className="fixed inset-0 z-[75] flex items-center justify-center bg-black/60 p-4" style={{pointerEvents:"auto", "--primary":"43 65% 62%", "--primary-foreground":"30 20% 6%"}} role="dialog" aria-modal="true" aria-labelledby="driver-important-notification-title" data-testid="driver-important-notification-modal">
      <div className="premium-card w-full max-w-sm max-h-[85dvh] overflow-y-auto border-primary/35 shadow-[0_0_44px_rgba(234,194,105,0.22)]">
        <div className="border-b border-primary/20 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-primary">{text.importantNotificationTitle}</p>
          <h2 id="driver-important-notification-title" className="mt-1 text-lg font-bold text-foreground">{notification.title}</h2>
          <p className="mt-2 text-xs text-muted-foreground">{text.importantNotificationPending}: {count}</p>
        </div>
        <div className="space-y-3 p-5">
          <p className="whitespace-pre-wrap text-sm leading-7 text-muted-foreground">{notification.body}</p>
          {notification.order_id && (
            <p className="text-xs text-muted-foreground">{text.orderNo}: {notification.order_id}</p>
          )}
          {notification.created_at && (
            <p className="text-xs text-muted-foreground">
              {text.importantNotificationCreatedAt}: {new Date(notification.created_at).toLocaleString(locale)}
            </p>
          )}
        </div>
        {error && <p role="alert" className="px-5 pb-3 text-sm text-red-300">{text.importantNotificationFailed}</p>}
        <div className="grid grid-cols-2 gap-3 border-t border-primary/20 p-4">
          <button
            type="button"
            onClick={onAcknowledge}
            disabled={busy} aria-busy={busy}
            className="premium-button border border-primary/25 bg-background py-3 text-foreground"
            data-testid="driver-important-notification-ack"
          >
            {busy ? text.importantNotificationSaving : text.importantNotificationAck}
          </button>
          <button type="button" disabled={busy} onClick={onOpenOrder} className="premium-button bg-primary py-3 text-primary-foreground">
            {text.importantNotificationOpenOrder}
          </button>
          <button type="button" onClick={onLater} data-testid="driver-notification-later" className="col-span-2 py-2 text-sm text-muted-foreground">
            {text.importantNotificationLater}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function DriverNoticeModal({ notice, text, onClose }) {
  const localizedNotice = getLocalizedDriverNotice(notice);

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/55 p-4" data-testid="driver-notice-auto-modal">
      <div className="premium-card w-full max-w-sm overflow-hidden">
        <div className="border-b p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-primary">{text.driverNoticeTitle}</p>
          <h2 className="mt-1 text-lg font-bold text-foreground">{localizedNotice.title}</h2>
        </div>
        <div className="max-h-[52vh] overflow-y-auto p-5">
          <p className="whitespace-pre-wrap text-sm leading-7 text-muted-foreground">{localizedNotice.body}</p>
        </div>
        <div className="border-t p-4">
          <button type="button" onClick={onClose} className="premium-button w-full bg-primary py-3 text-primary-foreground">
            {text.close}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
